import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class MiaV3Service {
  constructor(private http: HttpClient) {}

  obtenhaMetricas(filtro: { dias?: number, de?: string, ate?: string }):
    Promise<{ dias: number, resumo: any, tempos: any, anterior: any, motivos: any[] }> {
    return this.http.get('/miav3/metricas', { params: this.paramsDePeriodo(filtro) }).toPromise()
      .then((resposta: any) => ({
        dias: resposta?.dias || filtro.dias || 7,
        resumo: resposta?.resumo || null,
        tempos: resposta?.tempos || null,
        anterior: resposta?.anterior || null,
        motivos: resposta?.motivos || []
      }));
  }

  /** URL do CSV de conversas do período (download direto, autenticado pela sessão). */
  urlExportacaoDeConversas(filtro: { dias?: number, de?: string, ate?: string }): string {
    const params = new URLSearchParams(this.paramsDePeriodo(filtro));
    return `/miav3/exportar-conversas?${params.toString()}`;
  }

  /** de/até (YYYY-MM-DD) têm prioridade sobre dias — mesma regra do servidor. */
  private paramsDePeriodo(filtro: { dias?: number, de?: string, ate?: string }): any {
    if (filtro.de && filtro.ate) return { de: filtro.de, ate: filtro.ate };
    return { dias: String(filtro.dias || 7) };
  }

  obtenhaConversas(busca?: string, periodo?: { de?: string, ate?: string }): Promise<any[]> {
    const params: any = {};
    if (busca) params.busca = busca;
    if (periodo?.de) params.de = periodo.de;
    if (periodo?.ate) params.ate = periodo.ate;

    return this.http.get('/miav3/conversas', { params }).toPromise()
      .then((resposta: any) => resposta?.conversas || []);
  }

  obtenhaTurnos(conversaId: number): Promise<{ conversa: any, turnos: any[] }> {
    return this.http.get(`/miav3/conversas/${conversaId}/turnos`).toPromise()
      .then((resposta: any) => ({ conversa: resposta?.conversa, turnos: resposta?.turnos || [] }));
  }

  // ---- Tela de QA (avaliação da atuação da Mia — só admin do MeuCardápio) ----

  obtenhaAvaliacoes(filtro: { dias?: number, de?: string, ate?: string,
                              veredito?: string, categoria?: string }): Promise<any> {
    const params: any = this.paramsDePeriodo(filtro);
    if (filtro.veredito) params.veredito = filtro.veredito;
    if (filtro.categoria) params.categoria = filtro.categoria;
    return this.http.get('/miav3/avaliacoes', { params }).toPromise();
  }

  obtenhaNaoAvaliados(filtro: { dias?: number, de?: string, ate?: string }): Promise<any> {
    return this.http.get('/miav3/avaliacoes/nao-avaliados',
      { params: this.paramsDePeriodo(filtro) }).toPromise();
  }

  /** Gera o relatório narrativo do período (chamada de LLM; leva segundos). */
  gereRelatorioAvaliacao(filtro: { dias?: number, de?: string, ate?: string }): Promise<any> {
    return this.http.post('/miav3/avaliacoes/relatorio', this.paramsDePeriodo(filtro)).toPromise();
  }

  obtenhaRelatoriosAvaliacao(): Promise<any> {
    return this.http.get('/miav3/avaliacoes/relatorios').toPromise();
  }

  obtenhaConfigAvaliacao(): Promise<any> {
    return this.http.get('/miav3/avaliacoes/config').toPromise();
  }

  salveConfigAvaliacao(config: { ativa: boolean, desfechos: string, amostragemPedidoGerado: number }): Promise<any> {
    return this.http.put('/miav3/avaliacoes/config', config).toPromise();
  }

  /** Avaliação gravada + transcrição da janela do atendimento (drill-down). */
  obtenhaAvaliacao(atendimentoId: number): Promise<any> {
    return this.http.get(`/miav3/atendimentos/${atendimentoId}/avaliacao`).toPromise();
  }

  /** Dispara o juiz (avaliar ou reavaliar) — chamada de LLM, demora segundos. */
  avalieAtendimento(atendimentoId: number): Promise<any> {
    return this.http.post(`/miav3/atendimentos/${atendimentoId}/avaliar`, {}).toPromise();
  }

  marqueConversaComoLida(conversaId: number): Promise<any> {
    return this.http.post(`/miav3/conversas/${conversaId}/lida`, {}).toPromise();
  }

  assumaConversa(conversaId: number): Promise<any> {
    return this.http.post(`/miav3/conversas/${conversaId}/assumir`, {}).toPromise();
  }

  devolvaConversa(conversaId: number): Promise<any> {
    return this.http.post(`/miav3/conversas/${conversaId}/devolver`, {}).toPromise();
  }

  respondaConversa(conversaId: number, texto: string): Promise<any> {
    return this.http.post(`/miav3/conversas/${conversaId}/responder`, { texto }).toPromise();
  }

  /** Link público do widget de demonstração (gera o token na primeira consulta). */
  obtenhaLinkWidget(): Promise<any> {
    return this.http.get('/miav3/widget/link').toPromise();
  }

  /** Troca o token do widget — o link antigo morre na hora. */
  regenereTokenWidget(): Promise<any> {
    return this.http.post('/miav3/widget/regenerar', {}).toPromise();
  }

  envieTurnoSimulador(
    telefone: string,
    mensagem: string,
    simularPedidoAberto = false,
    imagem?: { base64: string; mimeType: string; legenda?: string }
  ): Promise<any> {
    // imagem vai como item {tipo:'imagem'} — o texto digitado vira a legenda (como no WhatsApp)
    const mensagens: any[] = imagem
      ? [{ tipo: 'imagem', base64: imagem.base64, mimeType: imagem.mimeType, legenda: imagem.legenda }]
      : [mensagem];
    return this.http.post('/miav3/simulador/turno', { telefone, mensagens, simularPedidoAberto }).toPromise();
  }

  /**
   * Áudio do cliente no simulador. Sem transcrição não há o que enviar: o
   * servidor só precisa saber que chegou um áudio (o player usa o blob local),
   * e responde com a reação do comportamento_audio da loja.
   */
  envieAudioSimulador(telefone: string, duracaoSegundos: number): Promise<any> {
    return this.http.post('/miav3/simulador/audio', { telefone, duracaoSegundos }).toPromise();
  }

  /** Desfaz o takeover que o áudio provocou e devolve a conversa à Mia. */
  devolvaSimuladorParaMia(telefone: string): Promise<any> {
    return this.http.post('/miav3/simulador/devolver', { telefone }).toPromise();
  }

  /** Sonda dos follow-ups proativos entregues pelo worker na transcrição. */
  obtenhaFollowupsSimulador(telefone: string, aposTurnoId: number): Promise<any> {
    return this.http.get('/miav3/simulador/followups',
      { params: { telefone, aposTurnoId: String(aposTurnoId) } }).toPromise();
  }

  /** Antecipa o próximo follow-up pendente da conversa (worker envia na hora). */
  adianteFollowupSimulador(telefone: string): Promise<any> {
    return this.http.post('/miav3/simulador/followups/adiantar', { telefone }).toPromise();
  }

  obtenhaOpcoesCheckout(): Promise<any> {
    return this.http.get('/miav3/simulador/checkout/opcoes').toPromise();
  }

  obtenhaEnderecosCheckout(): Promise<any> {
    return this.http.get('/miav3/simulador/checkout/enderecos').toPromise();
  }

  busqueCep(cep: string): Promise<any> {
    return this.http.get(`/miav3/simulador/checkout/cep/${cep}`).toPromise();
  }

  calculeTaxaCheckout(endereco: any, valor: number): Promise<any> {
    return this.http.post('/miav3/simulador/checkout/taxa', { endereco, valor }).toPromise();
  }

  envieLocalizacaoCheckout(telefone: string): Promise<any> {
    return this.http.post('/miav3/simulador/checkout/localizacao', { telefone }).toPromise();
  }

  /** Evento do checkout conversacional (iniciar/clique/texto/localização) → ações. */
  envieEventoCheckout(telefone: string, evento: any): Promise<any> {
    return this.http.post('/miav3/simulador/checkout/evento', { telefone, evento }).toPromise();
  }

  /** Confirmação final do wizard de pagamento (modo/endereço vêm do estado no servidor). */
  confirmeCheckout(dados: { telefone: string; formaDePagamentoId: number; trocoPara?: number }): Promise<any> {
    return this.http.post('/miav3/simulador/checkout/confirmar', dados).toPromise();
  }

  finalizeCheckout(dados: { telefone: string; modo: string; formaDePagamentoId: number;
                            trocoPara?: number; nomeContato?: string; endereco?: any }): Promise<any> {
    return this.http.post('/miav3/simulador/checkout', dados).toPromise();
  }
}
